import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Avatar,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import { Link } from "react-router-dom";
import { firestore } from "../firebase.config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getIsAdmin, setIsAdmin, getUserEmail } from "../utils/sessionCheck";
import { useUserContext } from "../context/UserContext";

const CustomAppBar = () => {
  const [anchor, setAnchor] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null); // For the profile menu
  const [isAdmin, setIsAdminState] = useState(false);
  const [userEmail, setUserEmailState] = useState("");
  const [geolocationAuthEnabled, setGeolocationAuthEnabled] = useState(false);

  const { handleLogout } = useUserContext();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const userRole = sessionStorage.getItem("userRole"); 


  const superAdminEmails = [
    process.env.REACT_APP_SUPERADMIN_EMAIL1,
    process.env.REACT_APP_SUPERADMIN_EMAIL2,
    process.env.REACT_APP_SUPERADMIN_EMAIL3,
    process.env.REACT_APP_SUPERADMIN_EMAIL4,
  ];

  // Fetch geolocation auth status
  useEffect(() => {
    const fetchGeolocationAuthStatus = async () => {
      try {
        const settingsDocRef = doc(firestore, "Settings", "geolocationAuth");
        const settingsDoc = await getDoc(settingsDocRef);

        if (settingsDoc.exists()) {
          const enabled = settingsDoc.data().enabled;
          setGeolocationAuthEnabled(enabled);
        } else {
          console.warn("Geolocation auth setting not found.");
        }
      } catch (error) {
        console.error("Error fetching geolocation auth setting:", error);
      }
    };

    fetchGeolocationAuthStatus();
  }, []);

  // Fetch user details and admin status
  useEffect(() => {
    const checkUserAdminStatus = async () => {
      const email = getUserEmail(); // Get the user's email from session storage
      if (email) {
        setUserEmailState(email); // Set the email in the state
  
        // Check if the user is an admin based on Firestore's Users collection
        const adminStatus = await setIsAdmin(email); // Uses your sessionCheck.js function
        setIsAdminState(adminStatus); // Update the state with the result

        // Check if the user is a super admin
        if (superAdminEmails.includes(email)) {
          setIsSuperAdmin(true);
        }
      }
    };
  
    checkUserAdminStatus();
  }, []);

  // Handle toggle change for geolocation auth
  const handleGeolocationToggle = async (event) => {
    const newValue = event.target.checked;
    setGeolocationAuthEnabled(newValue);

    try {
      const settingsDocRef = doc(firestore, "Settings", "geolocationAuth");
      await updateDoc(settingsDocRef, { enabled: newValue });
      console.log("Geolocation auth updated to:", newValue);
    } catch (error) {
      console.error("Error updating geolocation auth setting:", error);
    }
  };

  // Handle menu click
  const handleMenuClick = (event) => {
    setAnchor(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchor(null); // Ensure you close the menu
  };

  // Handle settings click
  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  // Handle settings close
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  // Handle profile click (for opening profile menu)
  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  // Handle profile menu close
  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  // Handle logout
  const handleLocalLogout = () => {
    // Use the context logout function to properly reset state
    handleLogout();
    sessionStorage.clear();
    window.location.href = "/"; // Redirect to the sign-in page
  };

  const handleAddNewClick = () => {
    window.location.href = '/timer'; // Change the path according to your route
  };

  return (
    <AppBar position="static" color="primary" sx={{
      bgcolor: "white",
      color: "black",
      width: "100%",         // span full width
      borderRadius: 0,       // remove rounding if you want flush edges
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
      top: 0,                // stick to top
      left: 0,
      right: 0,
    }}>
      <Toolbar sx={{ px: 3 }}>
        <Button 
          color="inherit" 
          onClick={handleMenuClick} 
          startIcon={<MenuIcon />}
          sx={{ color: "black" }}
        >
          Menu
        </Button>
        
        <Menu 
          anchorEl={anchor} 
          open={Boolean(anchor)} 
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              bgcolor: 'white',
              color: 'black',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            },
            '& .MuiMenuItem-root': {
              color: 'black',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.08)',
              },
            },
          }}
        >
          <MenuItem onClick={handleAddNewClick}>Home</MenuItem>
          <MenuItem component={Link} to="/assigntimer" onClick={handleMenuClose}>
            Go to Assigned Timer
          </MenuItem> {/* Apply for Leave option for all users */}
          <MenuItem component={Link} to="/report" onClick={handleMenuClose}>
            Report
          </MenuItem>
          <MenuItem component={Link} to="/timetable" onClick={handleMenuClose}>
            Timetable
          </MenuItem>
          <MenuItem component={Link} to="/applyleave" onClick={handleMenuClose}>
            Apply for Leave
          </MenuItem> {/* Apply for Leave option for all users */}
        </Menu>
        <Typography variant="h5" component="div" sx={{ textAlign: "center", flexGrow: 1, color: "black" }}>
          Timer
        </Typography>
        {isAdmin && (
          <Tooltip title="Settings">
            <IconButton color="inherit" onClick={handleSettingsClick} sx={{ color: "black" }}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}
        <Menu
          anchorEl={settingsAnchorEl}
          open={Boolean(settingsAnchorEl)}
          onClose={handleSettingsClose}
          sx={{
            '& .MuiPaper-root': {
              bgcolor: 'white',
              color: 'black',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            },
            '& .MuiMenuItem-root': {
              color: 'black',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.08)',
              },
            },
            '& .MuiFormControlLabel-root': {
              color: 'black',
            },
            '& .MuiSwitch-root': {
              '& .MuiSwitch-switchBase': {
                color: '#1976d2',
              },
              '& .MuiSwitch-track': {
                bgcolor: 'rgba(0, 0, 0, 0.2)',
              },
            },
          }}
        >
          <MenuItem component={Link} to="/assign" onClick={handleSettingsClose}>
            Assign Coordinates
          </MenuItem>
          <MenuItem component={Link} to="/task" onClick={handleSettingsClose}>
            Assign Task
          </MenuItem>
          <MenuItem component={Link} to="/formteam" onClick={handleSettingsClose}>
            Team Management
          </MenuItem>
          <MenuItem>
            <FormControlLabel
              control={
                <Switch
                  checked={geolocationAuthEnabled !== null ? geolocationAuthEnabled : false}
                  onChange={handleGeolocationToggle}
                  color="primary"
                />
              }
              label="Location"
            />
          </MenuItem>
          <MenuItem component={Link} to="/adduser" onClick={handleSettingsClose}>
            Add New User
          </MenuItem>
          <MenuItem component={Link} to="/leaveapproval" onClick={handleSettingsClose}>
            Leave Approval
          </MenuItem> {/* Leave Approval only for admins */}

          {(isSuperAdmin || isAdmin) && (
            <MenuItem component={Link} to="/usermanagement" onClick={handleSettingsClose}>
              User Management
            </MenuItem>
          )}
          
          {(isSuperAdmin || isAdmin) && (
            <MenuItem component={Link} to="/timermanagement" onClick={handleSettingsClose}>
              Timer Management
            </MenuItem>
          )}
          {isSuperAdmin && (
            <MenuItem component={Link} to="/tenantmanagement" onClick={handleSettingsClose}>
              Tenant Management
            </MenuItem>
          )}

        </Menu>
        <Tooltip title={userEmail} arrow>
        <IconButton onClick={handleProfileClick} sx={{ color: "black" }}>
          <PermIdentityIcon fontSize="Meduim" />
        </IconButton>
      </Tooltip>
        <Menu
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={handleProfileClose}
          sx={{
            '& .MuiPaper-root': {
              bgcolor: 'white',
              color: 'black',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            },
            '& .MuiMenuItem-root': {
              color: 'black',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.08)',
              },
            },
          }}
        >
          <MenuItem onClick={handleLocalLogout}>Logout</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default CustomAppBar;
